import { useEffect, useState } from 'react';
import { api, formatChips } from '../api.js';

/**
 * Results — every booked game, told from our own feed.
 *
 * This is what replaced the Ledger in the nav: it needs no Riot key and no
 * syncing, because everything on it (scoreline, result, the money that moved)
 * came through the agent and the book. A game appears the moment it settles.
 */

type ResultRow = Awaited<ReturnType<typeof api.results>>['games'][number];

function when(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ResultsPage() {
  const [games, setGames] = useState<ResultRow[] | null>(null);
  const [openGames, setOpenGames] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setOpenGames((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void api
        .results()
        .then((data) => {
          if (!cancelled) setGames(data.games);
        })
        .catch(() => {
          if (!cancelled) setGames([]);
        });
    load();
    const timer = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return (
    <div className="page">
      <header className="page-head">
        <h1>Results</h1>
        <p>Every game the book has taken, newest first — the score, and where the chips went.</p>
      </header>

      {games === null && <p className="note">Loading…</p>}
      {games?.length === 0 && <p className="note">No games yet. The first one settles, it shows up here.</p>}

      {games && games.length > 0 && (
        <ul className="results">
          {games.map((game) =>
            game.voided ? (
              <li key={game.id} className="result voided">
                <span className="result-when">{when(game.at)}</span>
                <span className="result-void-text">
                  Game never happened — dodge or remake. All bets refunded.
                </span>
              </li>
            ) : (
              <li key={game.id} className={`result ${game.result === 'win' ? 'win' : 'loss'}`}>
                <div className="result-row">
                  <span className={`result-stamp ${game.result === 'win' ? 'win' : 'loss'}`}>
                    {game.result === 'win' ? 'W' : 'L'}
                  </span>
                  <div className="result-main">
                    <span className="result-headline">
                      {game.champion ?? 'Ranked game'}
                      {game.kills !== null && (
                        <span className="num result-line">
                          {' '}
                          {game.kills}/{game.deaths}/{game.assists}
                        </span>
                      )}
                    </span>
                    <span className="result-sub">
                      {when(game.at)}
                      {game.durationSec !== null && ` · ${Math.round(game.durationSec / 60)} min`}
                      {game.account && ` · ${game.account}`}
                    </span>
                  </div>
                  <div className="result-money">
                    <span className="num result-staked">{formatChips(game.staked)}</span>
                    <span className="result-money-sub">
                      staked by {game.bettors} bettor{game.bettors === 1 ? '' : 's'}
                    </span>
                    {game.topWinner && (
                      <span className="result-topwin">
                        top win: <strong>{game.topWinner.name}</strong>{' '}
                        <span className="num up">+{formatChips(game.topWinner.profit)}</span>
                      </span>
                    )}
                  </div>
                </div>

                {game.wagers.length > 0 && (
                  <>
                    <button className="result-toggle" onClick={() => toggle(game.id)}>
                      {game.bets} bet{game.bets === 1 ? '' : 's'} {openGames.has(game.id) ? '▴' : '▾'}
                    </button>
                    {openGames.has(game.id) && (
                      <ul className="result-wagers">
                        {game.wagers.map((wager, i) => (
                          <li key={i}>
                            <span className="floor-who">{wager.who}</span>
                            <span className="num floor-stake">{formatChips(wager.stake)}</span>
                            <span className="floor-market">{wager.marketLabel}</span>
                            <span className={`num wager-outcome ${wager.status}`}>
                              {wager.status === 'won' && `+${formatChips(wager.profit)}`}
                              {wager.status === 'lost' && `−${formatChips(-wager.profit)}`}
                              {wager.status === 'void' && 'refunded'}
                              {wager.status === 'open' && 'riding'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
